/**
 * SRV-FG-5-4: Pending Service Requests Tab
 * Shown on WorksheetListPage — lists public pre-registration requests for staff review
 */

import {
  acceptServiceRequest,
  getPendingServiceRequests,
  rejectServiceRequest,
  type ServiceRequestData,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

export function PendingRequestsTab() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ServiceRequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await getPendingServiceRequests();
      setRequests(response.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRequests();
  }, [fetchRequests]);

  const handleAccept = async (id: string) => {
    setActionLoading(id);
    try {
      await acceptServiceRequest(id);
      await fetchRequests();
    } catch {
      // Error handling
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await rejectServiceRequest(id);
      await fetchRequests();
    } catch {
      // Error handling
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return t('service.pendingRequest.pending');
      case 'ACCEPTED':
        return t('service.pendingRequest.accepted');
      case 'REJECTED':
        return t('service.pendingRequest.rejected');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.pendingRequest.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('service.pendingRequest.noRequests')}</p>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div
                key={req.id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{req.customerName}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(req.status)}`}
                      >
                        {getStatusLabel(req.status)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{req.phone}</p>
                    {req.email && <p className="text-sm text-muted-foreground">{req.email}</p>}
                    <p className="text-sm">
                      <span className="text-muted-foreground">
                        {t('service.pendingRequest.equipment')}:
                      </span>{' '}
                      {req.equipmentDescription}
                    </p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">
                        {t('service.pendingRequest.problem')}:
                      </span>{' '}
                      {req.problemDescription}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(req.createdAt).toLocaleString('hu-HU')}
                      {req.preferredDate &&
                        ` | ${t('service.publicRequest.preferredDate')}: ${req.preferredDate}`}
                    </p>
                  </div>
                  {req.status === 'PENDING' && (
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(req.id)}
                        disabled={actionLoading === req.id}
                      >
                        {t('service.pendingRequest.accept')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(req.id)}
                        disabled={actionLoading === req.id}
                      >
                        {t('service.pendingRequest.reject')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
